import React, {useEffect, useRef, useState} from 'react';
import {Button} from "primereact/button";
import {InputText} from "primereact/inputtext";
import {Checkbox} from "primereact/checkbox";

import {UnAuthService} from "../../service/UnAuthService";
import {AppContext} from "../../HomePage";
import {getKeyValue} from "../../utils/Utils";
import AuthLayout from "./AuthLayout";
import AuthField from "./AuthField";

const RESEND_COOLDOWN_SECONDS = 60;

const TwoFactorChallenge = ({challenge, onSuccess, onCancel}) => {

    const {t, setError} = React.useContext(AppContext);

    const codeRef = useRef(null);
    const [code, setCode] = useState('');
    const [method, setMethod] = useState(challenge.method);
    const [maskedEmail, setMaskedEmail] = useState(challenge.masked_email);
    const [rememberDevice, setRememberDevice] = useState(false);
    const [formError, setFormError] = useState('');
    const [disabled, setDisabled] = useState(false);
    const [cooldown, setCooldown] = useState(method === 'email' ? RESEND_COOLDOWN_SECONDS : 0);

    useEffect(() => { codeRef.current && codeRef.current.focus(); }, []);

    useEffect(() => {
        if (cooldown <= 0)
            return;
        const timer = setTimeout(() => setCooldown(cooldown - 1), 1000);
        return () => clearTimeout(timer);
    }, [cooldown]);

    const showError = (error) => {
        const data = error.response && error.response.data;
        const translated = data && data.error_key ? t(data.error_key) : null;
        if (translated && translated !== data.error_key) {
            setFormError(translated);
        } else {
            setError(error);
            setError(null);
        }
    };

    const verify = (event) => {
        if (event)
            event.preventDefault();
        if (disabled)
            return;
        setFormError('');
        if (code.trim().length !== 6) {
            setFormError(t('login.two_factor.code_required'));
            return;
        }
        setDisabled(true);
        UnAuthService.twoFactorVerify(challenge.challenge_id, code.trim(), rememberDevice)
            .then(() => onSuccess())
            .catch(error => { showError(error); setDisabled(false); });
    };

    const resend = () => {
        setFormError('');
        UnAuthService.twoFactorResend(challenge.challenge_id).then(data => {
            setMaskedEmail(data.masked_email);
            setCooldown(RESEND_COOLDOWN_SECONDS);
        }).catch(showError);
    };

    const useEmailInstead = () => {
        setFormError('');
        UnAuthService.twoFactorFallbackEmail(challenge.challenge_id).then(data => {
            setMethod(data.method);
            setMaskedEmail(data.masked_email);
            setCooldown(RESEND_COOLDOWN_SECONDS);
        }).catch(showError);
    };

    const subtitle = method === 'email'
        ? t('login.two_factor.subtitle_email', {email: maskedEmail})
        : t('login.two_factor.subtitle_totp');

    return <AuthLayout title={t('login.two_factor.title')} subtitle={subtitle}>

        <form onSubmit={verify} noValidate>

            {formError && <div className="auth-ux__form-error" role="alert">{formError}</div>}

            <AuthField id="code" label={t('login.two_factor.code_label')}>
                <InputText id="code" name="code" inputMode="numeric" autoComplete="one-time-code"
                           maxLength={6} ref={codeRef} value={code}
                           onChange={(e) => setCode(getKeyValue(e).value.replace(/\D/g, ''))}/>
            </AuthField>

            <div className="auth-ux__field">
                <label className="auth-ux__terms">
                    <Checkbox inputId="remember_device" checked={rememberDevice}
                              onChange={(e) => setRememberDevice(e.checked)}/>
                    <span>{t('login.two_factor.remember_device')}</span>
                </label>
            </div>

            <Button type="submit" label={t('actions.verify')} icon="pi pi-check"
                    className="auth-ux__submit" disabled={disabled} loading={disabled}/>
        </form>

        <div className="auth-ux__inline-actions auth-ux__inline-actions--after-submit">
            {method === 'email' &&
                <button type="button" className="auth-ux__link" onClick={resend} disabled={cooldown > 0 || disabled}>
                    {cooldown > 0
                        ? t('login.two_factor.resend_in', {seconds: cooldown})
                        : t('login.two_factor.resend')}
                </button>}
            {method === 'totp' &&
                <button type="button" className="auth-ux__link" onClick={useEmailInstead} disabled={disabled}>
                    {t('login.two_factor.use_email')}
                </button>}
            <button type="button" className="auth-ux__link" onClick={onCancel}>
                {t('actions.cancel')}
            </button>
        </div>
    </AuthLayout>;
};
export default TwoFactorChallenge;
